import React, { useState, useEffect, useRef } from "react";
import { Users, Search, UserPlus, X, Check, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useUser } from "../../../context/UserContext";
import { useClasseContext } from "../../../context/ClasseContext";
import { useEleveWitouthGroup } from "../../../hooks/fetch_api_hook/useEleveWithoutGroup";
import { usePostApi } from "../../../hooks/post_api_hook/usePostApi";
import { estDemiGroupe } from "../../../utils/groupesDeBesoin";
import { url } from "../../../utils/confserveur";
import { useParams } from "react-router-dom";
import Loading from "../../general/Loading";

const AddElevesToGroup = ({ 
  show, 
  handleClose, 
  groupNom, 
  classeDate,
  handleAddEleveToGroup,
  handleMessagePost,
  handleReload
}) => {
  // Récupération des données depuis les contexts et hooks
  const { etablissementData, user } = useUser();
  const { classeDetail } = useClasseContext();
  const { idclasse } = useParams();
  // Demi-groupe (2026-09-30) : la liste propose les élèves de sa classe qui n'y
  // sont pas ; certains sont dans d'autres groupes, la plupart n'ont pas à y
  // entrer. Donc ni « sans groupe », ni « barrette », ni compteur qui pousserait
  // à les ajouter tous. Groupe de besoin : fenêtre inchangée.
  const demiGroupe = estDemiGroupe(classeDetail);
  const classeDuGroupe = classeDetail?.classes_detail?.[0];
  // États pour la recherche et sélection
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSelectedList, setShowSelectedList] = useState(true);
  
  // Refs
  const modalRef = useRef(null);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Chargement des élèves sans groupe
  const { data: elevesData, isLoading: isLoadingEleves, isError: isErrorEleves } =
    useEleveWitouthGroup(etablissementData?.id, classeDetail?.classes_detail, user?.matiere, true, idclasse);
  
  // Hook pour poster les affectations
  const [{ isLoading: isLoadingPost, isError: isErrorPost }, doPost] = 
    usePostApi(url + "groupes/affecter-eleves/");
  
  // Récupérer les élèves sans groupe
  const elevesWithoutGroup = elevesData?.eleves_sans_groupe || [];
  
  // Filtrer les élèves selon la recherche et exclure ceux déjà sélectionnés
  const filteredStudents = elevesWithoutGroup.filter(student => {
    const isAlreadySelected = selectedStudents.some(s => s.id === student.id);
    if (isAlreadySelected) return false;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      student.nom?.toLowerCase().includes(searchLower) ||
      student.prenom?.toLowerCase().includes(searchLower) 
    );
  });

  // Focus initial
  useEffect(() => {
    if (show) {
      modalRef.current?.focus();
      setTimeout(() => {
        searchRef.current?.focus();
      }, 100);
    }
  }, [show]);

  // Réinitialiser le formulaire
  useEffect(() => {
    if (show) {
      setSearchQuery('');
      setSelectedStudents([]);
      setErrors({});
      setHighlightedIndex(-1);
      setShowSuggestions(false);
      setShowSelectedList(true);
    }
  }, [show]);

  // Gestion du clic en dehors des suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  // Gestion de la recherche
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(value.length > 0);
    setHighlightedIndex(-1);
    
    if (errors.eleves) {
      setErrors(prev => ({ ...prev, eleves: '' }));
    }
  };

  // Sélection d'un élève
  const handleSelectStudent = (student) => {
    if (!selectedStudents.some(s => s.id === student.id)) {
      setSelectedStudents(prev => [...prev, student]);
      setSearchQuery('');
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      searchRef.current?.focus();
    }
  };

  // Retirer un élève de la sélection
  const handleRemoveStudent = (studentId) => {
    setSelectedStudents(prev => prev.filter(s => s.id !== studentId));
  };

  // Sélectionner tous les élèves filtrés
  const handleSelectAll = () => {
    const newStudents = filteredStudents.filter(student => 
      !selectedStudents.some(s => s.id === student.id)
    );
    setSelectedStudents(prev => [...prev, ...newStudents]);
    setSearchQuery('');
    setShowSuggestions(false);
  };

  // Navigation au clavier
  const handleKeyDownSearch = (e) => {
    if (!showSuggestions || filteredStudents.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredStudents.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredStudents.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredStudents.length) {
          handleSelectStudent(filteredStudents[highlightedIndex]);
        } else if (filteredStudents.length === 1) {
          handleSelectStudent(filteredStudents[0]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (selectedStudents.length === 0) {
      newErrors.eleves = 'Veuillez sélectionner au moins un élève';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const affectations = {
        affectations: [{ 
          groupId: idclasse, 
          elevesIds: selectedStudents.map(s => s.id)
        }]
      };
      
      const response = await doPost(affectations);
      
      if (!isErrorPost) {
        // Ajouter tous les élèves au groupe
        for (const student of selectedStudents) {
          await handleAddEleveToGroup(student);
        }
        
        const message = selectedStudents.length === 1 
          ? `1 élève ajouté au groupe avec succès`
          : `${selectedStudents.length} élèves ajoutés au groupe avec succès`;
        
        handleMessagePost(message, "success");
        handleReload();
        handleClose('success');
      } else {
        setErrors({ submit: "Erreur lors de l'ajout des élèves au groupe" });
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout:", error);
      setErrors({ submit: "Une erreur est survenue. Veuillez réessayer." });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gestion des touches globales
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isSubmitting) {
      handleClose();
    } else if (e.key === 'Enter' && e.ctrlKey && !isSubmitting) {
      handleSubmit();
    }
  };

  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4" 
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          handleClose();
        }
      }}
    >
      <div 
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="bg-white rounded-3xl shadow-2xl flex flex-col w-full max-w-2xl max-h-[90vh] overflow-hidden animate-in"
        style={{
          background: 'linear-gradient(135deg, #f8f9ff 0%, #f5f7ff 100%)',
          animation: 'slideIn 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div 
          className="relative w-full p-6 text-center text-white"
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          }}
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <Users className="w-8 h-8" />
            <h2 className="text-2xl font-semibold">
              Ajouter des élèves au groupe
            </h2>
          </div>
          <p className="text-sm opacity-90">
            Groupe : {groupNom} • {classeDate}
          </p>
          {classeDetail?.classes_detail && (
            <p className="text-xs mt-2 opacity-80">
              {demiGroupe ? 'Classe' : 'Classes de la barrette'} : {classeDetail.classes_detail.join(', ')}
            </p>
          )}
        </div>

        {/* Corps du modal */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Messages d'erreur */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <p>{errors.submit}</p>
            </div>
          )}

          {/* Contenu principal */}
          {isLoadingEleves ? (
            <div className="py-12">
              <Loading loadingText={demiGroupe ? "Chargement des élèves..." : "Chargement des élèves sans groupe..."} />
            </div>
          ) : isErrorEleves ? (
            <div className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600">Erreur lors du chargement des élèves</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Rafraîchir
              </button>
            </div>
          ) : (
            <>
              {/* Barre de recherche */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {demiGroupe ? "Rechercher un élève" : "Rechercher des élèves sans groupe"}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDownSearch}
                    onFocus={() => searchQuery && setShowSuggestions(true)}
                    disabled={isSubmitting}
                    className="w-full pl-10 pr-4 py-3 border-2 rounded-xl transition-all duration-200 
                      focus:outline-none focus:ring-0 text-base border-gray-200 
                      hover:border-gray-300 focus:border-green-500 focus:shadow-lg 
                      focus:shadow-green-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Tapez le nom, prénom ou classe..."
                  />
                </div>

                {/* Suggestions */}
                {showSuggestions && searchQuery && (
                  <div 
                    ref={suggestionsRef}
                    className=" mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-60 overflow-auto"
                    style={{ width: 'calc(100% - 3rem)', left: '1.5rem', right: '1.5rem' }}
                  >
                    {filteredStudents.length > 0 ? (
                      <>
                        <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {filteredStudents.length} élève(s) trouvé(s)
                          </span>
                          <button
                            onClick={handleSelectAll}
                            className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Tout sélectionner
                          </button>
                        </div>
                        {filteredStudents.map((student, index) => (
                          <div
                            key={student.id}
                            className={`px-4 py-3 cursor-pointer transition-colors
                              ${index === highlightedIndex ? 'bg-green-50 border-l-4 border-green-500' : 'hover:bg-gray-50'}
                              ${index !== 0 ? 'border-t border-gray-100' : ''}`}
                            onClick={() => handleSelectStudent(student)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                          >
                            <p className="font-semibold text-gray-900">
                              {student.nom} {student.prenom}
                            </p>
                            <p className="text-sm text-gray-500">
                              Classe : {student.classe}
                            </p>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="px-4 py-6 text-center text-gray-500">
                        {elevesWithoutGroup.length === 0 
                          ? (demiGroupe
                            ? `Tous les élèves de ${classeDuGroupe ?? 'la classe'} sont déjà dans le groupe`
                            : "Aucun élève sans groupe dans la barrette")
                          : selectedStudents.length === elevesWithoutGroup.length
                          ? "Tous les élèves sont déjà sélectionnés"
                          : "Aucun élève trouvé avec cette recherche"}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Liste des élèves sélectionnés */}
              {selectedStudents.length > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-medium text-gray-700">
                      Élèves sélectionnés ({selectedStudents.length})
                    </h3>
                    <button
                      onClick={() => setShowSelectedList(!showSelectedList)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {showSelectedList ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  {showSelectedList && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-xl p-3 max-h-60 overflow-y-auto">
                      <div className="space-y-2">
                        {selectedStudents.map(student => (
                          <div key={student.id} className="flex justify-between items-center bg-white rounded-lg p-2">
                            <div>
                              <p className="font-medium text-gray-900">
                                {student.nom} {student.prenom}
                              </p>
                              <p className="text-xs text-gray-500">
                                {student.classe}
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveStudent(student.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      {selectedStudents.length > 1 && (
                        <button
                          onClick={() => setSelectedStudents([])}
                          className="mt-2 text-xs text-red-600 hover:text-red-800"
                        >
                          Tout désélectionner
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {errors.eleves && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-red-700 text-sm font-medium">{errors.eleves}</p>
                </div>
              )}

              {/* Statistiques : groupe de besoin seulement. Pour un demi-groupe,
                  les élèves cochés sont déjà comptés au-dessus de la liste et
                  sur le bouton (épreuve de Musk, 2026-09-30). */}
              {!demiGroupe ? (
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total élèves sans groupe :</span>
                  <span className="font-medium">{elevesWithoutGroup.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Élèves sélectionnés :</span>
                  <span className="font-medium text-green-600">{selectedStudents.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Élèves restants :</span>
                  <span className="font-medium">{elevesWithoutGroup.length - selectedStudents.length}</span>
                </div>
              </div>
              ) : null}
            </>
          )}
        </div>

        {/* Footer avec boutons */}
        <div className="border-t border-gray-200 p-6 flex gap-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white
              rounded-xl font-semibold transition-all duration-300
              hover:from-orange-600 hover:to-orange-700 hover:-translate-y-0.5 text-base
              disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingEleves || selectedStudents.length === 0}
            className="flex-1 px-6 py-3 rounded-xl text-white font-semibold 
              transition-all duration-300 hover:-translate-y-0.5 tracking-wide 
              border-none disabled:opacity-50 disabled:cursor-not-allowed text-base
              flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)'
            }}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Ajout en cours...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                {selectedStudents.length === 1 
                  ? "Ajouter l'élève" 
                  : `Ajouter ${selectedStudents.length} élèves`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddElevesToGroup;